import { Logic } from '~common/ui-logic/logic'
import React from 'react'
import { Invoice } from '~/storage/types'
import { formatTimestampForDisplay } from '~common/utils/date-transformation/date-transformations'
import { ISO_TO_CURRENCY_SYMBOL } from '~common/utils/currency-normalization/currency-normalization'
import { AppContextType } from '~/app-context'
import type { DocumentTransactionAssociation } from '~/storage/types'
import { EmailAndInvoicePreview } from '~/features/email-preview/ui'

export interface InvoiceResultDependencies {
    invoiceDbId: Invoice['id'] | null
    onClick?: () => void
    onClickById?: (id: string) => void
    referenceLocation?: 'transactions' | 'emails' // is this document displayed on its own or referenced in another block? influences styles.
    hideAvatar?: boolean
    contextTransactionId?: string
    dontShowMatches?: boolean
    specificMatchConfidence?: number | null // New prop
    visibleColumns?: PossibleColumns[]
    context?: 'match-search'
    showAllConfirmedMatches?: boolean
    onToggleAllConfirmedMatches?: () => void
}

export type PossibleColumns =
    | 'date'
    | 'amount'
    | 'currency'
    | 'vendorName'
    | 'vendorEmail'
    | 'invoiceId'
    | 'reason'
    | 'createdAt'
    | 'matchConfidence'
    | 'category'
    | 'match'
    | 'referenceText'
    | 'note'

export type PossibleColumnsFormat = {
    date: string
    amount: number
    currency: string
    vendorName: string
    vendorEmail: string | null
    invoiceId: string
    reason: string
    createdAt: string
    matchConfidence: number | null
    category: string | null
    match: number | null
    referenceText: string
    note: string
}

export interface InvoiceResultState {
    isHovered: boolean
    showAllMatches: boolean
    showConfirmedMatches: boolean
}

export class InvoiceResultLogic extends Logic<
    InvoiceResultDependencies & AppContextType,
    InvoiceResultState
> {
    getInitialState = (): InvoiceResultState => ({
        isHovered: false,
        showAllMatches: false,
        showConfirmedMatches: false,
    })

    shouldReload(
        oldDeps: InvoiceResultDependencies,
        newDeps: InvoiceResultDependencies,
    ): boolean {
        return oldDeps.invoiceDbId !== newDeps.invoiceDbId
    }

    initialize = async () => {}

    setHovered = (hovered: boolean) => {
        this.setState({ isHovered: hovered })
    }

    handleClickOnResult = async () => {
        if (this.deps.onClick) {
            this.deps.onClick()
            return
        }
        if (this.deps.invoiceDbId) {
            // if (this.deps.context === 'match-search') {
            //     if (!this.deps.contextTransactionId) return
            //     await this.deps.services.transactionInvoiceMatching.confirmAssociation(
            //         this.deps.invoiceDbId,
            //         this.deps.contextTransactionId,
            //     )

            //     return
            // }

            this.deps.services.globalEvents.emit({
                type: 'spawnPane',
                render: () =>
                    React.createElement(EmailAndInvoicePreview, {
                        invoiceId: this.deps.invoiceDbId ?? undefined,
                        services: this.deps.services,
                        storageModules: this.deps.storageModules,
                    }),
                title: 'Invoice Preview',
                emailPreviewProps: {
                    invoiceId: this.deps.invoiceDbId ?? undefined,
                },
            })
        }
    }

    formatInvoiceData(
        invoice: Invoice | null,
        deps: Pick<
            InvoiceResultDependencies,
            | 'specificMatchConfidence'
            | 'referenceLocation'
            | 'contextTransactionId'
            | 'dontShowMatches'
        >,
    ): {
        date: string
        amount: number
        currency: string
        vendorName: string
        vendorEmail: string | null
        invoiceId: string
        reason: string
        createdAt: number
        matchConfidence: number | null
    } | null {
        if (!invoice) return null
        const cache = this.deps.services.cache
        const settings = cache.getKey('settings')
        const dateFormat = settings?.dateFormat ?? 'MM/DD/YYYY'
        const metadata = invoice.metadata

        // Get vendor email - first try from metadata, then from source email
        let vendorEmail: string | null = null
        if (
            metadata.vendorEmailAddresses &&
            metadata.vendorEmailAddresses.length > 0
        ) {
            vendorEmail = metadata.vendorEmailAddresses[0]
        } else if (invoice.sourceEmailId) {
            const emails = cache.getKey('emails')
            const sourceEmail = emails ? emails[invoice.sourceEmailId] : null
            if (sourceEmail) {
                vendorEmail = sourceEmail.fromEmail
            }
        }

        let matchConfidence: number | null = null
        if (deps.specificMatchConfidence !== undefined) {
            matchConfidence = deps.specificMatchConfidence
        } else if (invoice.id && !deps.dontShowMatches) {
            // Compute from cache
            const matchesByInvoice = cache.getKey('matchesByInvoice') || {}
            const assocIds = matchesByInvoice[invoice.id] || []
            const documentTransactionAssociations =
                cache.getKey('documentTransactionAssociations') || {}
            if (assocIds.length > 0) {
                // Take the highest confidence, prefer auto or user_confirmed
                const associations = assocIds
                    .map((id) => documentTransactionAssociations[id])
                    .filter((assoc): assoc is DocumentTransactionAssociation =>
                        Boolean(assoc),
                    )
                    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
                if (associations.length > 0) {
                    matchConfidence = associations[0].confidence
                }
            }

            // If referenceLocation is 'transactions', compute specific confidence for contextTransactionId
            if (
                deps.referenceLocation === 'transactions' &&
                deps.contextTransactionId &&
                invoice.id
            ) {
                const matchesByTransaction =
                    cache.getKey('matchesByTransaction') || {}
                const txAssocIds =
                    matchesByTransaction[deps.contextTransactionId] || []
                const associations = txAssocIds
                    .map((id) => documentTransactionAssociations[id])
                    .filter(
                        (assoc): assoc is DocumentTransactionAssociation =>
                            Boolean(assoc) && assoc.documentId === invoice.id,
                    )
                    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
                if (
                    associations.length > 0 &&
                    associations[0].method === 'auto'
                ) {
                    matchConfidence = associations[0].confidence
                }
            }
        }

        return {
            date: formatTimestampForDisplay(metadata.date, dateFormat),
            amount: metadata.amount,
            currency: ISO_TO_CURRENCY_SYMBOL[metadata.currency ?? ''],
            vendorName: metadata.vendorName || 'Unknown Vendor',
            vendorEmail,
            invoiceId: metadata.invoiceId || '',
            reason: metadata.reason,
            createdAt: invoice.createdAt,
            matchConfidence,
        }
    }

    confirmMatch = async (documentId: string, transactionId: string) => {
        await this.deps.services.transactionInvoiceMatching.confirmAssociation(
            documentId,
            transactionId,
        )
    }

    removeMatch = async (transactionId: string, documentId: string) => {
        await this.deps.services.transactionInvoiceMatching.removeAssociation(
            transactionId,
            documentId,
        )
    }

    confirmMatches = async () => {
        if (!this.deps.invoiceDbId) return

        const cache = this.deps.services.cache
        const matchesByInvoice = cache.getKey('matchesByInvoice') || {}
        const assocIds = matchesByInvoice[this.deps.invoiceDbId] || []
        const documentTransactionAssociations =
            cache.getKey('documentTransactionAssociations') || {}

        const autoAssociations = assocIds
            .map((id) => documentTransactionAssociations[id])
            .filter(
                (assoc): assoc is DocumentTransactionAssociation =>
                    assoc && assoc.method === 'auto',
            )

        if (autoAssociations.length === 0) return

        const updatePromises = autoAssociations.map((assoc) =>
            this.deps.storageModules.matchingAssociations.updateAssociation(
                assoc.id,
                { method: 'user_confirmed' },
            ),
        )

        try {
            await Promise.all(updatePromises)
            // Cache will update automatically via Firestore listener
            this.setHovered(false)
        } catch (error) {
            console.error('Failed to confirm matches:', error)
            // Optionally track error with errorTracker
            this.deps.services.errorTracker.trackError(
                'Confirm matches failed',
                error as Error,
            )
        }
    }

    toggleShowAllMatches = () => {
        this.setState({ showAllMatches: !this.state.showAllMatches })
    }

    toggleShowConfirmedMatches = () => {
        this.setState({
            showConfirmedMatches: !this.state.showConfirmedMatches,
        })
    }
}
